import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { BACKEND_AGENTS_API } from '../env';
import { askAgent, connectAgentSocket, isAgentSocketConfigured, waitForChart } from './AgentSocket';

// Espera máxima de un Análisis Profundo por WebSocket: time_budget_seconds
// (840 como máximo, ver analysis_config.py) + el timeout de 900 s de
// AgentRuntimeFunction + margen.
export const DEEP_ANALYSIS_TIMEOUT_MS = 16 * 60 * 1000;

// Recuperación de una respuesta cuyo WebSocket se cerró a mitad del turno
// (en el teléfono, al cambiar de app o bloquear la pantalla): el turno sigue
// en el servidor, que guarda la respuesta si no la pudo entregar, y acá se
// consulta GET /agent/turns/{invocationId} hasta que esté lista.
const RECOVERY_POLL_INTERVAL_MS = 4000;
const RECOVERY_TIMEOUT_MS = 6 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Modos del chat que se envían como analysis_mode (los demás son un turno normal).
const LONG_MODES = ['deep', 'dashboard'];

/**
 * Mensaje `completed`/`error` guardado por el servidor -> mismo resultado que
 * devuelve askAgent(). null mientras no esté listo.
 */
export const turnMessageToResult = (message) => {
    if (!message) return null;
    if (message.type === 'completed') {
        return { success: true, data: message };
    }
    return {
        success: false,
        error: message.message || message.error || 'Error al invocar el agente',
        errorCode: message.error,
        errorReason: message.reason,
        retryAfterSeconds: message.retry_after_seconds,
        invocation_id: message.invocation_id
    };
};

/**
 * Consulta GET /agent/turns/{key} hasta que el mensaje guardado esté listo.
 * Devuelve el mensaje tal cual, o null si no llegó a tiempo.
 */
const pollTurnMessage = async (key, {
    timeoutMs = RECOVERY_TIMEOUT_MS,
    intervalMs = RECOVERY_POLL_INTERVAL_MS,
    fetchStatus,
    now = () => Date.now(),
    wait = sleep,
    signal
} = {}) => {
    const deadline = now() + timeoutMs;
    while (now() < deadline && !signal?.aborted) {
        try {
            const body = await fetchStatus(key);
            if (body?.status === 'ready') {
                return body.message;
            }
        } catch (error) {
            // Red intermitente al volver del segundo plano: se reintenta.
            console.warn('No se pudo consultar el resultado del turno, reintentando', error);
        }
        if (signal?.aborted) {
            break;
        }
        await wait(intervalMs);
    }
    // Cancelado (el usuario cambió o borró la conversación): no se consulta más.
    if (signal?.aborted) {
        return null;
    }
    // Un teléfono congela la página con la pantalla bloqueada: al volver, el
    // plazo puede haber vencido aunque la respuesta ya esté guardada. Una
    // última consulta antes de rendirse.
    try {
        const body = await fetchStatus(key);
        if (body?.status === 'ready') {
            return body.message;
        }
    } catch (error) {
        console.warn('No se pudo consultar el resultado del turno', error);
    }
    return null;
};

/**
 * Espera la respuesta de un turno cuya conexión se perdió. Devuelve el
 * resultado (igual que askAgent) o null si no llegó a tiempo.
 * `fetchStatus` es inyectable para tests.
 */
export const recoverTurnResult = async (invocationId, options = {}) => (
    turnMessageToResult(await pollTurnMessage(invocationId, options))
);

/**
 * Respuesta de una pregunta que quedó sin contestar en el historial (la
 * página se recargó o se cerró mientras el turno corría en el servidor).
 * Espera, como mucho, lo que le queda al turno desde que se envió
 * (`askedAt`); si ya pasó, consulta una vez: el servidor guarda la
 * respuesta 24 h. Devuelve el resultado (igual que askAgent) o null.
 * `signal` (AbortSignal) corta la espera, p. ej. al cambiar de conversación.
 */
export const recoverPendingTurn = async (invocationId, { askedAt, analysisMode = 'basic', fetchStatus = fetchTurnStatus, now = () => Date.now(), ...pollOptions } = {}) => {
    const maxWaitMs = LONG_MODES.includes(analysisMode) ? DEEP_ANALYSIS_TIMEOUT_MS : RECOVERY_TIMEOUT_MS;
    const remainingMs = askedAt ? askedAt + maxWaitMs - now() : maxWaitMs;
    return turnMessageToResult(await pollTurnMessage(invocationId, {
        timeoutMs: Math.max(remainingMs, 0), fetchStatus, now, ...pollOptions
    }));
};

const fetchTurnStatus = async (invocationId) => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    const response = await axios.get(
        `${BACKEND_AGENTS_API}/agent/turns/${encodeURIComponent(invocationId)}`,
        { headers: { Authorization: token } }
    );
    return response.data;
};

// La gráfica de un turno por WebSocket se genera en el servidor y tarda a lo
// sumo el Timeout de ChartDataFunction (120 s); si la conexión se cayó, se
// recupera de GET /agent/turns/{invocationId}-chart.
const CHART_RECOVERY_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Mensaje "chart" -> mismo resultado que ChartDataAPI.getChartData():
 * {queryResults, chart}, o lanza un error con status/reason (los mismos que
 * usa chartErrorMessage) si el servidor no pudo generarla.
 */
export const chartMessageToResult = (message) => {
    if (!message) {
        throw new Error('No llegó la gráfica de esta respuesta.');
    }
    if (message.status !== 200) {
        const error = new Error(message.message || message.error || 'No se pudo generar la gráfica');
        error.status = message.status;
        error.code = message.error;
        error.reason = message.reason;
        throw error;
    }
    return {
        queryResults: message.queryResults || [],
        chart: message.chart ?? null,
        // Créditos que cobró la gráfica (se suman a los del turno en el chat).
        credits_consumed: message.credits_consumed
    };
};

/**
 * Gráfica de un turno que el servidor genera y envía por WebSocket
 * (completed.chart_pending). Primero la espera por la conexión; si se cerró
 * o no llegó, la recupera por HTTP. NO la pide a POST /agent/chart-data: el
 * servidor ya la está generando (y cobrando).
 * `waitOnSocket`/`fetchStatus` son inyectables para tests.
 */
export const awaitTurnChart = async (invocationId, {
    waitOnSocket = waitForChart,
    fetchStatus = fetchTurnStatus,
    ...pollOptions
} = {}) => {
    const message = await waitOnSocket(invocationId);
    if (message) {
        return chartMessageToResult(message);
    }
    const recovered = await pollTurnMessage(`${invocationId}-chart`, {
        timeoutMs: CHART_RECOVERY_TIMEOUT_MS, fetchStatus, ...pollOptions
    });
    return chartMessageToResult(recovered);
};


/**
 * Invoca un agente de Bedrock a través de la Lambda
 * @param {Object} params - Parámetros de la invocación
 * @param {string} params.prompt - Pregunta del usuario
 * @param {string} params.sessionId - ID de la sesión
 * @param {string} params.workspaceId - ID del workspace
 * @param {string} params.workspaceName - Nombre del workspace
 * @param {string} [params.agentConfigId] - config_id del agente elegido en el sidebar (opcional)
 * @param {boolean} [params.enableTrace] - Habilitar trazas (opcional)
 * @param {Object} [params.sessionAttributes] - Atributos de sesión (opcional)
 * @param {Function} [params.onProgress] - Progreso del agente ({stage, label}); solo por WebSocket
 * @param {'basic'|'deep'|'dashboard'} [params.analysisMode] - 'deep' = Análisis Profundo, 'dashboard' = tablero (solo por WebSocket)
 * @param {string|null} [params.modelId] - modelo elegido en el selector del chat (ID de Bedrock); null = el del agente
 * @param {Function} [params.onAccepted] - recibe el invocation_id al aceptarse la pregunta (solo por WebSocket)
 * @returns {Promise<Object>} Respuesta de la invocación
 *
 * Usa el API WebSocket (sin el límite de 29 s de API Gateway) cuando está
 * configurado; si no logra conectarse, cae al POST /agent/invoke de siempre.
 */
export const invokeAgent = async ({
    prompt,
    sessionId,
    workspaceId,
    workspaceName,
    agentConfigId = null,
    enableTrace = false,
    sessionAttributes = {},
    onProgress = null,
    analysisMode = 'basic',
    modelId = null,
    onAccepted = null
}) => {
    // Modos que duran minutos: solo por WebSocket y con espera extendida.
    const isLongMode = LONG_MODES.includes(analysisMode);
    try {
        // Get auth token
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        if (!token) {
            throw new Error('No authentication token available');
        }

        // Prepare request body
        const requestBody = {
            prompt,
            session_id: sessionId,
            workspace_id: workspaceId,
            enable_trace: enableTrace,
            session_attributes: {
                ...sessionAttributes,
                workspaceId,
                workspaceName
            }
        };

        // Agente específico elegido por el usuario (opcional). agent-runtime
        // valida que pertenezca al workspace/cuenta; si no, usa el default.
        if (agentConfigId) {
            requestBody.agent_config_id = agentConfigId;
        }
        if (isLongMode) {
            requestBody.analysis_mode = analysisMode;
        }
        // Modelo elegido en el chat (opcional): agent-runtime lo valida contra
        // "Modelos configurados" y responde model_not_available si ya no está.
        if (modelId) {
            requestBody.model_id = modelId;
        }

        if (isAgentSocketConfigured()) {
            let socketReady = false;
            try {
                await connectAgentSocket();
                socketReady = true;
            } catch (socketError) {
                console.warn('WebSocket de agentes no disponible, usando HTTP:', socketError);
            }
            // Una vez enviada por WebSocket, la pregunta NO se reintenta por
            // HTTP aunque falle: el turno puede estar corriendo (y cobrando)
            // del lado del servidor.
            if (socketReady) {
                // chart_via_ws: el servidor genera la gráfica al terminar el
                // turno y la envía por esta conexión (awaitTurnChart), sin el
                // límite de 29 s de POST /agent/chart-data.
                const result = await askAgent({ ...requestBody, chart_via_ws: true }, {
                    onProgress,
                    onAccepted,
                    ...(isLongMode ? { timeoutMs: DEEP_ANALYSIS_TIMEOUT_MS } : {})
                });
                console.log('Agent invocation response (WebSocket):', result);
                // Conexión perdida con el turno ya aceptado: el servidor lo
                // termina igual (y cobra), así que se recupera la respuesta
                // en vez de darla por perdida.
                if (!result.success && result.errorCode === 'connection_lost' && result.invocation_id) {
                    onProgress?.({ stage: 'recovering', label: 'Se cortó la conexión; recuperando la respuesta…' });
                    const recovered = await recoverTurnResult(result.invocation_id, {
                        fetchStatus: fetchTurnStatus,
                        ...(isLongMode ? { timeoutMs: DEEP_ANALYSIS_TIMEOUT_MS } : {})
                    });
                    if (recovered) {
                        console.log('Respuesta recuperada tras perder la conexión:', recovered);
                        return recovered;
                    }
                }
                return result;
            }
        }

        // Análisis Profundo y Dashboard duran minutos: por HTTP chocarían con
        // el límite de 29 s de API Gateway (y el backend los rechaza con 400).
        if (isLongMode) {
            const name = analysisMode === 'dashboard' ? 'El modo Dashboard' : 'El Análisis Profundo';
            return {
                success: false,
                error: `${name} necesita la conexión en tiempo real del chat. Recarga la página e inténtalo de nuevo.`,
                errorCode: analysisMode === 'dashboard' ? 'dashboard_requires_websocket' : 'deep_analysis_requires_websocket'
            };
        }

        console.log('Invoking agent via Lambda:', requestBody);

        // Call Lambda via API Gateway
        const response = await axios.post(
            `${BACKEND_AGENTS_API}/agent/invoke`,
            requestBody,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            }
        );

        console.log('Agent invocation response:', response.data);

        return {
            success: true,
            data: response.data
        };

    } catch (error) {
        console.error('Error invoking agent:', error);

        // Extract error message/code
        // El backend devuelve {"error": "<code>", "message": "<texto legible>"}
        // para errores de negocio (p. ej. no_data_connections) -- se
        // conserva el code aparte para que la UI pueda reaccionar sin
        // parsear el texto, y se prefiere `message` (legible) sobre
        // `error` (code) para mostrar al usuario.
        const errorCode = error.response?.data?.error;
        let errorMessage = 'Error al invocar el agente';
        if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (errorCode) {
            errorMessage = errorCode;
        } else if (error.message) {
            errorMessage = error.message;
        }

        return {
            success: false,
            error: errorMessage,
            errorCode,
            errorReason: error.response?.data?.reason,
            retryAfterSeconds: error.response?.data?.retry_after_seconds,
            invocation_id: error.response?.data?.invocation_id
        };
    }
};

/**
 * Obtiene el historial de invocaciones de un usuario
 * @param {string} userId - ID del usuario
 * @param {number} limit - Límite de resultados
 * @returns {Promise<Object>} Historial de invocaciones
 */
export const getUserInvocationHistory = async (userId, limit = 50) => {
    try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        const response = await axios.get(
            `${BACKEND_AGENTS_API}/agent/invocations/user/${userId}`,
            {
                headers: {
                    'Authorization': token
                },
                params: { limit }
            }
        );

        return {
            success: true,
            data: response.data
        };

    } catch (error) {
        console.error('Error getting invocation history:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

export default {
    invokeAgent,
    getUserInvocationHistory
};
